import type { AudioSource } from '../types.ts';
import { countryCodeToFlag, formatBitrate } from '../api.ts';

// ─── Audio Player ─────────────────────────────────────────────────────────────

export class AudioPlayer {
  private audio: HTMLAudioElement;
  private analyser: AnalyserNode | null = null;
  private audioCtx: AudioContext | null = null;
  private canvas: HTMLCanvasElement;
  private canvasCtx: CanvasRenderingContext2D;
  private rafHandle: number | null = null;
  private fakePhase = 0;

  // UI refs
  private playBtn: HTMLElement;
  private prevBtn: HTMLElement;
  private nextBtn: HTMLElement;
  private volumeInput: HTMLInputElement;
  private npLabel: HTMLElement;
  private stationPanel: HTMLElement;
  private stationFlag: HTMLElement;
  private stationName: HTMLElement;
  private stationLocation: HTMLElement;
  private stationTags: HTMLElement;
  private stationCodec: HTMLElement;
  private stationBitrate: HTMLElement;
  private stationLang: HTMLElement;

  // Callbacks
  private onPrevCallback: (() => void) | null = null;
  private onNextCallback: (() => void) | null = null;
  private onPlayPauseCallback: ((playing: boolean) => void) | null = null;

  // State
  private _current: AudioSource | null = null;
  private _isPlaying = false;
  private _volume = 0.8;

  constructor() {
    this.audio = new Audio();
    this.audio.crossOrigin = 'anonymous';
    this.audio.preload = 'none';

    this.canvas = document.getElementById('visualizer') as HTMLCanvasElement;
    this.canvasCtx = this.canvas.getContext('2d')!;

    this.playBtn = document.getElementById('btn-play')!;
    this.prevBtn = document.getElementById('btn-prev')!;
    this.nextBtn = document.getElementById('btn-next')!;
    this.volumeInput = document.getElementById('volume') as HTMLInputElement;
    this.npLabel = document.getElementById('np-label')!;
    this.stationPanel = document.getElementById('station-panel')!;
    this.stationFlag = document.getElementById('station-flag')!;
    this.stationName = document.getElementById('station-name')!;
    this.stationLocation = document.getElementById('station-location')!;
    this.stationTags = document.getElementById('station-tags')!;
    this.stationCodec = document.getElementById('station-codec')!;
    this.stationBitrate = document.getElementById('station-bitrate')!;
    this.stationLang = document.getElementById('station-language')!;

    this.attachListeners();
    this.startVisualizerLoop();
  }

  get current(): AudioSource | null { return this._current; }
  get isPlaying(): boolean { return this._isPlaying; }
  get volume(): number { return this._volume; }

  onPrev(cb: () => void): this { this.onPrevCallback = cb; return this; }
  onNext(cb: () => void): this { this.onNextCallback = cb; return this; }
  onPlayPause(cb: (playing: boolean) => void): this { this.onPlayPauseCallback = cb; return this; }

  /** Load and play a new audio source */
  async play(source: AudioSource): Promise<void> {
    this._current = source;
    this.updateStationPanel(source);
    this.npLabel.textContent = source.title.toUpperCase();

    // Fade out previous audio
    await this.fadeVolume(0, 300);
    this.audio.pause();
    this.audio.src = '';

    // Attempt Web Audio API connection for visualizer
    this.tryConnectWebAudio();

    this.audio.src = source.streamUrl;
    this.audio.volume = 0;

    try {
      await this.audio.play();
      this._isPlaying = true;
      this.playBtn.textContent = '⏸';
      this.onPlayPauseCallback?.(true);
      await this.fadeVolume(this._volume, 600);
    } catch (err) {
      console.warn('Audio play failed:', err);
      this._isPlaying = false;
      this.playBtn.textContent = '▶';
    }
  }

  /** Toggle play / pause */
  async togglePlay(): Promise<void> {
    if (!this._current) return;
    if (this._isPlaying) {
      await this.fadeVolume(0, 300);
      this.audio.pause();
      this._isPlaying = false;
      this.playBtn.textContent = '▶';
      this.onPlayPauseCallback?.(false);
    } else {
      this.audio.volume = 0;
      await this.audio.play();
      await this.fadeVolume(this._volume, 400);
      this._isPlaying = true;
      this.playBtn.textContent = '⏸';
      this.onPlayPauseCallback?.(true);
    }
  }

  setVolume(v: number): void {
    this._volume = Math.max(0, Math.min(1, v));
    if (this._isPlaying) this.audio.volume = this._volume;
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private attachListeners(): void {
    this.playBtn.addEventListener('click', () => void this.togglePlay());
    this.prevBtn.addEventListener('click', () => this.onPrevCallback?.());
    this.nextBtn.addEventListener('click', () => this.onNextCallback?.());

    this.volumeInput.addEventListener('input', () => {
      const v = parseInt(this.volumeInput.value, 10) / 100;
      this.setVolume(v);
    });

    this.audio.addEventListener('error', () => {
      this.npLabel.textContent = 'STREAM ERROR — TRY ANOTHER STATION';
      this._isPlaying = false;
      this.playBtn.textContent = '▶';
    });

    this.audio.addEventListener('waiting', () => {
      this.npLabel.textContent = `BUFFERING… ${this._current?.title.toUpperCase() ?? ''}`;
    });

    this.audio.addEventListener('playing', () => {
      if (this._current) this.npLabel.textContent = this._current.title.toUpperCase();
    });
  }

  private tryConnectWebAudio(): void {
    try {
      if (!this.audioCtx) {
        this.audioCtx = new AudioContext();
        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 128;
        const src = this.audioCtx.createMediaElementSource(this.audio);
        src.connect(this.analyser);
        this.analyser.connect(this.audioCtx.destination);
      }
      if (this.audioCtx.state === 'suspended') {
        void this.audioCtx.resume();
      }
    } catch {
      // CORS restriction — fall back to fake visualizer
      this.analyser = null;
    }
  }

  /** Cancel the visualizer animation loop (call when destroying the player) */
  destroy(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    void this.audioCtx?.close();
  }

  private startVisualizerLoop(): void {
    const draw = (): void => {
      this.rafHandle = requestAnimationFrame(draw);
      this.drawVisualizer();
    };
    draw();
  }

  private drawVisualizer(): void {
    const { width, height } = this.canvas;
    const ctx = this.canvasCtx;
    ctx.clearRect(0, 0, width, height);

    if (!this._isPlaying) {
      this.drawIdleLine(ctx, width, height);
      return;
    }

    if (this.analyser) {
      const buf = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(buf);
      this.drawBars(ctx, buf, width, height);
    } else {
      this.drawFakeWave(ctx, width, height);
    }
  }

  private drawBars(ctx: CanvasRenderingContext2D, buf: Uint8Array, w: number, h: number): void {
    const barW = w / buf.length;
    const grad = ctx.createLinearGradient(0, h, 0, 0);
    grad.addColorStop(0, '#d4a85388');
    grad.addColorStop(0.6, '#d4a853cc');
    grad.addColorStop(1, '#f0c96b');
    ctx.fillStyle = grad;

    for (let i = 0; i < buf.length; i++) {
      const barH = (buf[i] / 255) * h;
      ctx.fillRect(i * barW + 1, h - barH, barW - 2, barH);
    }
  }

  private drawFakeWave(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    this.fakePhase += 0.06;
    const mid = h / 2;
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, '#d4a85366');
    grad.addColorStop(0.5, '#f0c96bbb');
    grad.addColorStop(1, '#d4a85366');
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x < w; x++) {
      const freq = 0.03 + Math.sin(this.fakePhase * 0.2) * 0.01;
      const amp = 10 + Math.sin(this.fakePhase + x * 0.08) * 4;
      const y = mid + Math.sin(x * freq + this.fakePhase) * amp
        + Math.sin(x * freq * 2.3 + this.fakePhase * 1.3) * (amp * 0.4);
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  private drawIdleLine(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const mid = h / 2;
    ctx.strokeStyle = '#d4a85333';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(w, mid);
    ctx.stroke();
  }

  private fadeVolume(target: number, durationMs: number): Promise<void> {
    return new Promise((resolve) => {
      const start = this.audio.volume;
      const diff = target - start;
      const steps = 20;
      const stepMs = durationMs / steps;
      let step = 0;
      const id = setInterval(() => {
        step++;
        this.audio.volume = Math.max(0, Math.min(1, start + diff * (step / steps)));
        if (step >= steps) { clearInterval(id); resolve(); }
      }, stepMs);
    });
  }

  private updateStationPanel(source: AudioSource): void {
    this.stationPanel.classList.remove('hidden');
    this.stationFlag.textContent = countryCodeToFlag(source.countrycode);
    this.stationName.textContent = source.title;
    this.stationLocation.textContent =
      [source.country, source.language].filter(Boolean).join(' · ');

    // Tags
    this.stationTags.innerHTML = source.tags
      .map((t) => `<span class="tag">${t}</span>`)
      .join('');

    // Tech badges
    this.stationCodec.textContent = source.codec ?? '';
    this.stationCodec.style.display = source.codec ? '' : 'none';
    this.stationBitrate.textContent = formatBitrate(source.bitrate);
    this.stationBitrate.style.display = source.bitrate ? '' : 'none';
    this.stationLang.textContent = source.language;
    this.stationLang.style.display = source.language ? '' : 'none';

    // Signal strength based on votes
    const pct = Math.min(100, ((source.votes ?? 0) / 200) * 100);
    (document.getElementById('signal-strength') as HTMLElement).style.width = `${pct}%`;
  }
}
